import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Sparkles, TrendingUp, TrendingDown, Brain, Database, Quote,
  CheckCircle2, Circle, Loader2, Gauge, Target, MessageSquare, BookOpen, Globe, MapPin,
  HelpCircle, Calculator, Handshake,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { useLang, LANGS } from "../lib/i18n";
import { buildStory, storyT, journeyT } from "../lib/journey";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";

// Narration Démo jury — 100% isolée, FR/EN/ES. Aucun texte produit réel n'est modifié.
const NARR = {
  fr: {
    acts: ["Le problème", "Le copilote", "Comprendre", "La décision", "L'action", "Le guide", "Récapitulatif", "La vision"],
    a1a: "Chaque jour, des dirigeants décident avec des informations dispersées.",
    a1b: "LOCALBOOST transforme ces informations en décisions concrètes.",
    promise: "ANALYSER. DÉCIDER. AGIR. MESURER.",
    a2ask: "Mon chiffre d'affaires baisse. Où dois-je agir en priorité ?",
    a2reply: "J'ai compris. Analysons ce qui explique cette baisse et identifions votre priorité.",
    a2key: "Une seule porte d'entrée : votre activité. LOCALBOOST vous guide vers la bonne action.",
    youLabel: "Vous", copilotLabel: "Copilote LOCALBOOST",
    a3flow: ["Document", "Analyse", "Faits fiables", "Priorité"],
    bFait: "FAIT MESURÉ", bCalc: "CALCUL LOCALBOOST", bInterp: "INTERPRÉTATION", bHypo: "HYPOTHÈSE À VÉRIFIER",
    a3key: "Vos données deviennent des décisions, sans perdre leur provenance.",
    a4signal: "Signal", a4prio: "Priorité #1",
    a4quote: "Un dirigeant n'a pas besoin d'un rapport de plus. Il a besoin de savoir quoi faire lundi matin.",
    a5reco: "Action recommandée", a5module: "Module adapté", a5others: "Et toutes les autres capacités, au bon moment :",
    a5key: "Vous ne choisissez pas parmi une multitude d'outils. LOCALBOOST vous conduit vers celui qu'il vous faut.",
    a6title: "Le guide", a6a: "Vous n'avez pas besoin de tout apprendre aujourd'hui. Le guide vous accompagne, à votre rythme.",
    a6b: "Découvrez chaque fonction quand vous en avez besoin.", a6badge: "Toujours accessible depuis la navigation",
    a7vision1: "LOCALBOOST ne remplace pas le dirigeant. Il l'aide à décider mieux, plus vite et avec confiance.",
    a7vision2: "LOCALBOOST est opérationnel aujourd'hui. Sa feuille de route évoluera avec les besoins réels des dirigeants, sans jamais sacrifier la simplicité.",
    next: "Suivant", back: "Précédent", finish: "Créer mon compte", demoBadge: "DÉMO",
    seeWhy: "Voir pourquoi", seeCalc: "Voir le calcul", hideDetail: "Masquer",
    whyData: "Données utilisées", whyConstat: "Constat", whyPriority: "Priorité identifiée", whyHypo: "Hypothèse à vérifier",
    calcNote: "Estimation, non garantie de résultat.",
    recapTitle: "En moins de 2 minutes, LOCALBOOST :",
    recapItems: ["Analyse les données", "Détecte le problème", "Estime le potentiel", "Identifie la priorité", "Propose l'action", "Crée un plan 30 / 60 / 90 jours"],
    signalTitle: "Premier signal de marché", signalText: "Après avoir découvert LOCALBOOST AI, un conseiller de la CCISM a spontanément pris contact pour échanger sur l'application et les possibilités de collaboration avec la CCISM.", signalDate: "Échange exploratoire en cours — prise de contact le 18 août 2026.",
    preuveTitle: "Validation terrain en cours", preuveText: "Premier test pilote en cours — résultats bientôt disponibles.",
  },
  en: {
    acts: ["The problem", "The copilot", "Understand", "The decision", "The action", "The guide", "Recap", "The vision"],
    a1a: "Every day, business owners decide with scattered information.",
    a1b: "LOCALBOOST turns that information into concrete decisions.",
    promise: "ANALYZE. DECIDE. ACT. MEASURE.",
    a2ask: "My sales are dropping. Where should I act first?",
    a2reply: "Got it. Let's analyze what's driving this drop and find your priority.",
    a2key: "One entry point: your business. LOCALBOOST guides you to the right action.",
    youLabel: "You", copilotLabel: "LOCALBOOST Copilot",
    a3flow: ["Document", "Analysis", "Reliable facts", "Priority"],
    bFait: "MEASURED FACT", bCalc: "LOCALBOOST CALCULATION", bInterp: "INTERPRETATION", bHypo: "HYPOTHESIS TO VERIFY",
    a3key: "Your data becomes decisions — without losing its source.",
    a4signal: "Signal", a4prio: "Priority #1",
    a4quote: "An owner doesn't need another report. They need to know what to do Monday morning.",
    a5reco: "Recommended action", a5module: "Right module", a5others: "And every other capability, at the right time:",
    a5key: "You don't pick among many tools. LOCALBOOST takes you to the one you need.",
    a6title: "The guide", a6a: "You don't need to learn everything today. The guide supports you, at your pace.",
    a6b: "Discover each feature when you need it.", a6badge: "Always available from the navigation",
    a7vision1: "LOCALBOOST doesn't replace the owner. It helps them decide better, faster, with confidence.",
    a7vision2: "LOCALBOOST works today. Its roadmap will grow with owners' real needs, never sacrificing simplicity.",
    next: "Next", back: "Back", finish: "Create my account", demoBadge: "DEMO",
    seeWhy: "See why", seeCalc: "See the calculation", hideDetail: "Hide",
    whyData: "Data used", whyConstat: "Finding", whyPriority: "Identified priority", whyHypo: "Hypothesis to verify",
    calcNote: "Estimate, not a guarantee of results.",
    recapTitle: "In under 2 minutes, LOCALBOOST:",
    recapItems: ["Analyzes the data", "Detects the problem", "Estimates the potential", "Identifies the priority", "Proposes the action", "Creates a 30/60/90-day plan"],
    signalTitle: "First market signal", signalText: "After discovering LOCALBOOST AI, an advisor from the CCISM spontaneously reached out to discuss the application and possibilities for collaboration with the CCISM.", signalDate: "Exploratory discussion underway — first contact on 18 August 2026.",
    preuveTitle: "Field validation in progress", preuveText: "First pilot test underway — results coming soon.",
  },
  es: {
    acts: ["El problema", "El copiloto", "Comprender", "La decisión", "La acción", "La guía", "Resumen", "La visión"],
    a1a: "Cada día, los responsables deciden con información dispersa.",
    a1b: "LOCALBOOST convierte esa información en decisiones concretas.",
    promise: "ANALIZAR. DECIDIR. ACTUAR. MEDIR.",
    a2ask: "Mis ventas bajan. ¿Dónde debo actuar primero?",
    a2reply: "Entendido. Analicemos qué explica esta bajada e identifiquemos tu prioridad.",
    a2key: "Una sola puerta de entrada: tu negocio. LOCALBOOST te guía hacia la acción correcta.",
    youLabel: "Tú", copilotLabel: "Copiloto LOCALBOOST",
    a3flow: ["Documento", "Análisis", "Hechos fiables", "Prioridad"],
    bFait: "HECHO MEDIDO", bCalc: "CÁLCULO LOCALBOOST", bInterp: "INTERPRETACIÓN", bHypo: "HIPÓTESIS POR VERIFICAR",
    a3key: "Tus datos se convierten en decisiones, sin perder su origen.",
    a4signal: "Señal", a4prio: "Prioridad #1",
    a4quote: "Un responsable no necesita otro informe. Necesita saber qué hacer el lunes por la mañana.",
    a5reco: "Acción recomendada", a5module: "Módulo adecuado", a5others: "Y todas las demás capacidades, en el momento justo:",
    a5key: "No eliges entre muchas herramientas. LOCALBOOST te lleva a la que necesitas.",
    a6title: "La guía", a6a: "No necesitas aprenderlo todo hoy. La guía te acompaña, a tu ritmo.",
    a6b: "Descubre cada función cuando la necesites.", a6badge: "Siempre accesible desde la navegación",
    a7vision1: "LOCALBOOST no reemplaza al responsable. Le ayuda a decidir mejor, más rápido y con confianza.",
    a7vision2: "LOCALBOOST está operativo hoy. Su hoja de ruta evolucionará con las necesidades reales, sin sacrificar la simplicidad.",
    next: "Siguiente", back: "Atrás", finish: "Crear mi cuenta", demoBadge: "DEMO",
    seeWhy: "Ver por qué", seeCalc: "Ver el cálculo", hideDetail: "Ocultar",
    whyData: "Datos utilizados", whyConstat: "Constatación", whyPriority: "Prioridad identificada", whyHypo: "Hipótesis a verificar",
    calcNote: "Estimación, sin garantía de resultados.",
    recapTitle: "En menos de 2 minutos, LOCALBOOST:",
    recapItems: ["Analiza los datos", "Detecta el problema", "Estima el potencial", "Identifica la prioridad", "Propone la acción", "Crea un plan de 30/60/90 días"],
    signalTitle: "Primera señal del mercado", signalText: "Tras descubrir LOCALBOOST AI, un asesor de la CCISM se puso en contacto espontáneamente para hablar sobre la aplicación y las posibilidades de colaboración con la CCISM.", signalDate: "Conversación exploratoria en curso — primer contacto el 18 de agosto de 2026.",
    preuveTitle: "Validación de campo en curso", preuveText: "Primer test piloto en curso — resultados próximamente.",
  },
};

const TOTAL = 8;

// Bloc repliable "Voir pourquoi" / "Voir le calcul" — 100% statique, isolé Démo.
const Disclosure = ({ label, hideLabel, testid, icon: Icon, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2" data-testid={`${testid}-wrap`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={testid}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-neon hover:text-white transition-colors">
        <Icon size={13} /> {open ? hideLabel : label}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }} className="overflow-hidden" data-testid={`${testid}-content`}>
            <div className="mt-2 rounded-xl bg-white/5 border border-white/10 p-3 space-y-2">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function DemoStory() {
  const nav = useNavigate();
  const { data } = useApp();
  const { lang, setLang } = useLang();
  const [act, setAct] = useState(0);

  const S = storyT(lang);
  const J = journeyT(lang);
  const N = NARR[lang] || NARR.fr;
  const story = useMemo(() => buildStory(data, lang), [data, lang]);

  React.useEffect(() => { if (!data?.demoProfileId) nav("/demo", { replace: true }); }, [data, nav]);
  if (!story) return null;

  const locale = { fr: "fr-FR", en: "en-US", es: "es-ES" }[lang] || "fr-FR";
  const fmtInt = (n) => (n == null || n === "" ? "—" : new Intl.NumberFormat(locale).format(Number(n)));
  const eur = (n) => (n == null || n === "" ? "—" : `${fmtInt(n)} €`);
  const trendUp = Number(story.kpis.trend) >= 0;

  const next = () => setAct((a) => Math.min(TOTAL - 1, a + 1));
  const back = () => setAct((a) => Math.max(0, a - 1));

  const Flow = ({ steps }) => (
    <div className="flex items-center flex-wrap gap-2" data-testid="story-flow">
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          <motion.span initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 * i }}
            className="text-xs font-semibold px-3 py-1.5 rounded-full bg-neon/10 border border-neon/25 text-neon">{s}</motion.span>
          {i < steps.length - 1 && <ArrowRight size={14} className="text-neon/50" />}
        </React.Fragment>
      ))}
    </div>
  );

  const Badge = ({ children, cls }) => (
    <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full border ${cls}`}>{children}</span>
  );

  return (
    <div className="relative min-h-screen overflow-x-hidden grain flex flex-col" data-testid="demo-story">
      <div className="fixed inset-0 pointer-events-none z-0"><div className="absolute inset-0 aurora" /><div className="absolute inset-0 tech-grid opacity-60" /></div>

      <header className="z-30 glass border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <Logo />
          <div className="flex items-center gap-2 min-w-0">
            <span className="hidden md:inline text-xs font-semibold text-white/90 truncate max-w-[140px]" data-testid="story-business-name">{story.company.name}</span>
            <div className="flex items-center gap-0.5 rounded-lg bg-white/5 border border-white/10 p-0.5 shrink-0" data-testid="story-lang">
              {LANGS.map((l) => (
                <button key={l.code} type="button" onClick={() => setLang(l.code)} data-testid={`story-lang-${l.code}`}
                  className={`text-[11px] font-bold px-1.5 py-1 rounded-md transition-colors ${lang === l.code ? "bg-neon/20 text-neon" : "text-white/50 hover:text-white"}`}>
                  {l.code.toUpperCase()}
                </button>
              ))}
            </div>
            <button data-testid="story-change-activity" onClick={() => nav("/demo")}
              className="text-xs text-[hsl(var(--muted-foreground))] hover:text-white flex items-center gap-1 shrink-0">
              <ArrowLeft size={14} /> <span className="hidden sm:inline">{S.changeActivity}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Progression discrète 1/7 */}
      <div className="z-20 max-w-3xl mx-auto w-full px-4 pt-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-neon tracking-wide" data-testid="story-progress-label">{act + 1}/{TOTAL} · {N.acts[act]}</span>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-neon/15 text-neon font-semibold">{N.demoBadge}</span>
        </div>
        <div className="flex gap-1.5" data-testid="story-progress">
          {Array.from({ length: TOTAL }).map((_, i) => (
            <button key={i} type="button" onClick={() => setAct(i)} aria-current={i === act}
              data-testid={`story-progress-dot-${i}`} className="flex-1 min-h-[44px] sm:min-h-0 flex items-center group">
              <span className="w-full h-2 sm:h-1 rounded-full overflow-hidden bg-white/15 group-hover:bg-white/25 transition-colors">
                <span className={`block h-full transition-all duration-500 ${i <= act ? "bg-gradient-to-r from-primary to-neon w-full" : "w-0"} ${i === act ? "shadow-[0_0_8px_rgba(0,229,255,0.7)]" : ""}`} />
              </span>
            </button>
          ))}
        </div>
      </div>

      <main className="relative z-10 flex-1 max-w-3xl mx-auto w-full px-4 py-6 overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div key={act} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }}>

            {/* ACTE 1 — Le problème */}
            {act === 0 && (
              <div className="min-h-[52vh] flex flex-col items-center justify-center text-center gap-6" data-testid="act-1">
                <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                  className="font-display font-black text-2xl sm:text-4xl leading-tight max-w-2xl">{N.a1a}</motion.p>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
                  className="text-base sm:text-lg text-neon font-semibold max-w-xl">{N.a1b}</motion.p>
                <motion.p initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.6 }}
                  className="font-display font-black tracking-wide text-sm sm:text-base text-white/90">{N.promise}</motion.p>
              </div>
            )}

            {/* ACTE 2 — Le copilote */}
            {act === 1 && (
              <div className="space-y-4 pt-4" data-testid="act-2">
                <div className="flex justify-end">
                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                    className="max-w-[80%] rounded-2xl rounded-tr-sm bg-white/8 border border-white/10 p-3.5" data-testid="act2-ask">
                    <p className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">{N.youLabel}</p>
                    <p className="text-sm">{N.a2ask}</p>
                  </motion.div>
                </div>
                <div className="flex justify-start">
                  <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.7 }}
                    className="max-w-[85%] beam p-[1px]" data-testid="act2-reply">
                    <div className="rounded-2xl rounded-tl-sm bg-[var(--lb-card)] p-3.5 flex items-start gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-neon flex items-center justify-center shrink-0"><Brain size={16} className="text-white" /></div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-neon mb-1">{N.copilotLabel}</p>
                        <p className="text-sm font-medium">{N.a2reply}</p>
                      </div>
                    </div>
                  </motion.div>
                </div>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
                  className="lb-card p-4 flex items-center gap-2.5" data-testid="act2-key">
                  <MessageSquare size={16} className="text-neon shrink-0" />
                  <p className="text-sm font-medium">{N.a2key}</p>
                </motion.div>
              </div>
            )}

            {/* ACTE 3 — Données → Compréhension (réutilise story.understanding + provenance) */}
            {act === 2 && (
              <div className="space-y-4" data-testid="act-3">
                <Flow steps={N.a3flow} />
                <div className="lb-card p-4 flex items-start gap-3 border border-neon/20" data-testid="act3-fait">
                  <div className="w-8 h-8 rounded-lg bg-neon/15 text-neon flex items-center justify-center shrink-0 mt-0.5"><Database size={16} /></div>
                  <div>
                    <Badge cls="bg-neon/15 text-neon border-neon/30">{N.bFait}</Badge>
                    <p className="text-sm mt-1.5 leading-snug">{S.kRevenue} : <b>{eur(story.kpis.revenue)}</b> · {S.kTrend} : <b className={trendUp ? "text-emerald-400" : "text-rose-400"}>{trendUp ? "+" : ""}{story.kpis.trend} %</b> · {S.kMargin} : <b>{story.kpis.margin} %</b> · {S.kBasket} : <b>{eur(story.kpis.basket)}</b></p>
                  </div>
                </div>
                <div className="lb-card p-4 flex items-start gap-3" data-testid="act3-calcul">
                  <div className="w-8 h-8 rounded-lg bg-violet-500/15 text-violet-300 flex items-center justify-center shrink-0 mt-0.5"><Gauge size={16} /></div>
                  <div>
                    <Badge cls="bg-violet-500/15 text-violet-300 border-violet-400/30">{N.bCalc}</Badge>
                    <p className="text-sm mt-1.5 leading-snug">{story.diagnostic.impactEco}</p>
                    <Disclosure testid="act3-calc" label={N.seeCalc} hideLabel={N.hideDetail} icon={Calculator}>
                      <p className="text-sm"><span className="text-white/80">{story.diagnostic.calc?.formula}</span> <span className="text-neon font-bold"> = {story.diagnostic.calc?.result}</span></p>
                      <p className="text-[11px] text-[hsl(var(--muted-foreground))]">{N.calcNote}</p>
                    </Disclosure>
                  </div>
                </div>
                <div className="beam p-[1px]" data-testid="act3-interp"><div className="rounded-[1.1rem] bg-[var(--lb-card)] p-4 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-neon flex items-center justify-center shrink-0 mt-0.5"><Brain size={16} className="text-white" /></div>
                  <div>
                    <Badge cls="bg-blue-500/15 text-blue-300 border-blue-400/30">{N.bInterp}</Badge>
                    <p className="text-sm font-semibold mt-1.5 leading-snug">{story.understanding.insight}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2 flex items-center gap-1.5"><Quote size={12} /> <Badge cls="bg-amber-500/15 text-amber-300 border-amber-400/30">{N.bHypo}</Badge> <span className="ml-1">{story.diagnostic.origine}</span></p>
                  </div>
                </div></div>
                <p className="text-sm text-neon font-semibold text-center pt-1" data-testid="act3-key">{N.a3key}</p>
              </div>
            )}

            {/* ACTE 4 — La décision (réutilise story.diagnostic) */}
            {act === 3 && (
              <div className="space-y-4" data-testid="act-4">
                <div className="flex items-center gap-2 flex-wrap" data-testid="act4-morph">
                  <span className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[hsl(var(--muted-foreground))]">{N.a4signal} : {story.diagnostic.constat}</span>
                  <ArrowRight size={14} className="text-neon/60" />
                  <span className="text-xs px-3 py-1.5 rounded-full bg-neon/10 border border-neon/25 text-neon font-semibold">{N.a4prio}</span>
                </div>
                <motion.div initial={{ scale: 0.97, opacity: 0.6 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }}
                  className="beam p-[1px]" data-testid="act4-priority"><div className="rounded-[1.3rem] bg-[var(--lb-card)] p-6">
                  <p className="text-[11px] uppercase tracking-wide text-neon flex items-center gap-1.5"><Target size={14} /> {N.a4prio}</p>
                  <p className="font-display font-black text-xl sm:text-2xl leading-tight mt-2">{story.diagnostic.priority?.title}</p>
                  {story.diagnostic.priority?.impactShort && <p className="text-sm text-neon mt-2">{story.diagnostic.priority.impactShort}</p>}
                  <div className="mt-3">
                    <Disclosure testid="act4-why" label={N.seeWhy} hideLabel={N.hideDetail} icon={HelpCircle}>
                      <div><p className="text-[10px] uppercase tracking-wide text-neon">{N.whyData}</p>
                        <p className="text-xs mt-0.5">{S.kRevenue}: <b>{eur(story.kpis.revenue)}</b> · {S.kTrend}: <b className={trendUp ? "text-emerald-400" : "text-rose-400"}>{trendUp ? "+" : ""}{story.kpis.trend} %</b> · {S.kBasket}: <b>{eur(story.kpis.basket)}</b> · {S.kCustomers}: <b>{fmtInt(story.kpis.customers)}</b></p></div>
                      <div><p className="text-[10px] uppercase tracking-wide text-white/60">{N.whyConstat}</p><p className="text-xs mt-0.5">{story.diagnostic.constat}</p></div>
                      <div><p className="text-[10px] uppercase tracking-wide text-white/60">{N.whyPriority}</p><p className="text-xs mt-0.5 font-semibold">{story.diagnostic.priority?.title}</p></div>
                      <div><p className="text-[10px] uppercase tracking-wide text-amber-300">{N.whyHypo}</p><p className="text-xs mt-0.5">{story.diagnostic.origine}</p></div>
                    </Disclosure>
                    <Disclosure testid="act4-calc" label={N.seeCalc} hideLabel={N.hideDetail} icon={Calculator}>
                      <p className="text-sm"><span className="text-white/80">{story.diagnostic.calc?.formula}</span> <span className="text-neon font-bold"> = {story.diagnostic.calc?.result}</span></p>
                      <p className="text-[11px] text-[hsl(var(--muted-foreground))]">{N.calcNote}</p>
                    </Disclosure>
                  </div>
                </div></motion.div>
                {/* Plan 30/60/90 compact */}
                <div className="grid sm:grid-cols-3 gap-2.5" data-testid="act4-plan">
                  {story.plan.phases.map((p, i) => (
                    <div key={i} className="lb-card p-3" data-testid={`act4-phase-${i}`}>
                      <span className="text-[11px] font-bold uppercase tracking-wide text-neon">{p.label}</span>
                      <p className="text-xs font-medium leading-snug mt-1">{p.action}</p>
                    </div>
                  ))}
                </div>
                <div className="beam p-[1px]" data-testid="act4-quote"><div className="rounded-[1.2rem] bg-[var(--lb-card)] p-5 text-center">
                  <p className="font-display font-black text-base sm:text-lg leading-snug">"{N.a4quote}"</p>
                </div></div>
              </div>
            )}

            {/* ACTE 5 — Décision → Action → Module (réutilise story.execution.modules) */}
            {act === 4 && (
              <div className="space-y-4" data-testid="act-5">
                <div className="flex items-center gap-2 flex-wrap" data-testid="act5-chain">
                  <span className="text-xs px-3 py-1.5 rounded-full bg-neon/10 border border-neon/25 text-neon font-semibold">{N.a4prio}</span>
                  <ArrowRight size={14} className="text-neon/60" />
                  <span className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10">{N.a5reco}</span>
                  <ArrowRight size={14} className="text-neon/60" />
                  <span className="text-xs px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 font-semibold">{N.a5module} : {story.execution.modules[0]}</span>
                </div>
                <div className="lb-card p-4" data-testid="act5-modules">
                  <p className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2.5">{N.a5others}</p>
                  <div className="flex flex-wrap gap-2">
                    {story.execution.modules.map((m, i) => (
                      <span key={i} className={`text-xs font-medium px-3 py-1.5 rounded-full border ${i === 0 ? "bg-emerald-500/15 border-emerald-400/30 text-emerald-300" : "bg-neon/10 border-neon/25 text-neon"}`} data-testid={`act5-module-${i}`}>{m}</span>
                    ))}
                  </div>
                </div>
                <Disclosure testid="act5-why" label={N.seeWhy} hideLabel={N.hideDetail} icon={HelpCircle}>
                  <div><p className="text-[10px] uppercase tracking-wide text-neon">{N.whyData}</p>
                    <p className="text-xs mt-0.5">{S.kRevenue}: <b>{eur(story.kpis.revenue)}</b> · {S.kTrend}: <b className={trendUp ? "text-emerald-400" : "text-rose-400"}>{trendUp ? "+" : ""}{story.kpis.trend} %</b> · {S.kBasket}: <b>{eur(story.kpis.basket)}</b></p></div>
                  <div><p className="text-[10px] uppercase tracking-wide text-white/60">{N.whyConstat}</p><p className="text-xs mt-0.5">{story.diagnostic.constat}</p></div>
                  <div><p className="text-[10px] uppercase tracking-wide text-white/60">{N.whyPriority}</p><p className="text-xs mt-0.5 font-semibold">{story.diagnostic.priority?.title}</p></div>
                  <div><p className="text-[10px] uppercase tracking-wide text-amber-300">{N.whyHypo}</p><p className="text-xs mt-0.5">{story.diagnostic.origine}</p></div>
                </Disclosure>
                <p className="text-sm text-neon font-semibold text-center pt-1" data-testid="act5-key">{N.a5key}</p>
              </div>
            )}

            {/* ACTE 6 — Le guide (filet de sécurité) */}
            {act === 5 && (
              <div className="min-h-[46vh] flex flex-col items-center justify-center gap-5 text-center" data-testid="act-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-neon flex items-center justify-center"><BookOpen size={26} className="text-white" /></div>
                <p className="font-display font-black text-xl sm:text-2xl">{N.a6title}</p>
                <p className="text-sm sm:text-base max-w-xl">{N.a6a}</p>
                <p className="text-sm text-neon font-semibold max-w-lg">{N.a6b}</p>
                <span className="text-[11px] px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[hsl(var(--muted-foreground))] flex items-center gap-1.5" data-testid="act6-badge"><BookOpen size={12} /> {N.a6badge}</span>
              </div>
            )}

            {/* ACTE 7 — Récapitulatif (avant le prix) */}
            {act === 6 && (
              <div className="min-h-[46vh] flex flex-col items-center justify-center gap-6 text-center" data-testid="act-recap">
                <p className="font-display font-black text-xl sm:text-2xl max-w-xl">{N.recapTitle}</p>
                <div className="w-full max-w-md space-y-2.5" data-testid="recap-list">
                  {N.recapItems.map((it, i) => (
                    <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 * i }}
                      className="flex items-center gap-2.5 lb-card p-3 text-left" data-testid={`recap-item-${i}`}>
                      <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
                      <span className="text-sm font-medium">{it}</span>
                    </motion.div>
                  ))}
                </div>
                <p className="font-display font-black tracking-wide text-sm sm:text-lg text-white/90" data-testid="recap-promise">{N.promise}</p>
                <div className="lb-card p-4 max-w-md w-full flex items-start gap-2.5 border border-neon/40" data-testid="recap-signal">
                  <Handshake size={16} className="text-neon shrink-0 mt-0.5" />
                  <div className="text-left">
                    <p className="text-[11px] uppercase tracking-wide text-neon font-semibold">{N.signalTitle}</p>
                    <p className="text-sm mt-0.5">{N.signalText}</p>
                    <p className="text-xs mt-1.5 text-[hsl(var(--muted-foreground))]">{N.signalDate}</p>
                  </div>
                </div>
                <div className="lb-card p-4 max-w-md w-full flex items-start gap-2.5 border border-neon/20" data-testid="recap-proof">
                  <MapPin size={16} className="text-neon shrink-0 mt-0.5" />
                  <div className="text-left">
                    <p className="text-[11px] uppercase tracking-wide text-neon font-semibold">{N.preuveTitle}</p>
                    <p className="text-sm mt-0.5">{N.preuveText}</p>
                  </div>
                </div>
              </div>
            )}

            {/* ACTE 8 — Vision */}
            {act === 7 && (
              <div className="space-y-6 text-center pt-2" data-testid="act-7">
                <div className="flex items-center justify-center gap-2 text-sm font-semibold" data-testid="act7-langs">
                  <Globe size={16} className="text-neon" /> FRANÇAIS · ENGLISH · ESPAÑOL
                </div>
                <p className="font-display font-black tracking-wide text-base sm:text-xl text-white/90">{N.promise}</p>
                <p className="font-display font-black text-lg sm:text-2xl leading-tight max-w-2xl mx-auto">{N.a7vision1}</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-xl mx-auto">{N.a7vision2}</p>
                <div className="beam p-[1px] max-w-md mx-auto" data-testid="demo-pricing"><div className="rounded-[1.2rem] bg-[var(--lb-card)] p-5">
                  <p className="font-display font-bold text-sm mb-1">{J.readyTitle}</p>
                  <p className="font-display font-black text-3xl neon-text leading-none my-1" data-testid="demo-pricing-amount">{J.readyPrice}</p>
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-3">{J.readyConditions}</p>
                  <Button onClick={() => nav("/register")} data-testid="demo-pricing-cta" className="w-full rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold text-sm px-3 whitespace-normal leading-tight">{J.readyCta} <ArrowRight size={15} className="ml-1.5 shrink-0" /></Button>
                </div></div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      <footer className="z-30 glass border-t border-white/5 sticky bottom-0">
        <div className="max-w-3xl mx-auto px-4 pt-3 pb-3 flex items-center gap-3" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
          <Button variant="outline" onClick={back} disabled={act === 0} data-testid="story-back"
            className="rounded-full h-11 px-5 border-white/10 bg-white/5 disabled:opacity-40">
            <ArrowLeft size={16} className="mr-1.5" /> {N.back}
          </Button>
          {act < TOTAL - 1 ? (
            <Button onClick={next} data-testid="story-next"
              className="flex-1 rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold">
              {N.next} <ArrowRight size={16} className="ml-1.5" />
            </Button>
          ) : (
            <Button onClick={() => nav("/register")} data-testid="story-finish"
              className="flex-1 rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold">
              {N.finish} <ArrowRight size={16} className="ml-1.5" />
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
}
